// Types and Data for Entrepreneurship & Civic Engagement survey
// Measures entrepreneurial mindset and civic responsibility - GROWNi's core values

import { v4 as uuidv4 } from 'uuid'
import type { Timestamp } from '../firebase.ts'
import type { UserId } from '../ids.ts'
import { SurveyTypes } from '../Survey.ts'
import { SurveyQuestionTypes } from './SelfKnowledgeSurvey.ts'

export type EntrepreneurshipSurveyId = string

export interface EntrepreneurshipSurvey {
    id: EntrepreneurshipSurveyId
    surveyType: SurveyTypes.ENTREPRENEURSHIP
    userId: UserId
    hash?: string
    questions: EntrepreneurshipQuestionType[]
    scenarios?: EntrepreneurshipScenario[]
    allItems?: EntrepreneurshipSurveyItem[] // Questions + scenarios shuffled for presentation
    scores: EntrepreneurshipScores
    entrepreneurshipScore: number // 0-100
    civicEngagementScore: number // 0-100
    isDraft?: boolean
    createdAt: Timestamp
    updatedAt?: Timestamp
    deletedAt?: Timestamp | null
}

// Two main categories with subcategories
export enum EntrepreneurshipCategory {
    // Entrepreneurship dimensions
    INITIATIVE = 'initiative', // Iniciatíva
    RISK_TAKING = 'risk_taking', // Ochota riskovať
    INNOVATION = 'innovation', // Inovácia
    PROBLEM_SOLVING = 'problem_solving', // Riešenie problémov

    // Civic Engagement dimensions
    COMMUNITY_INVOLVEMENT = 'community_involvement', // Komunitná angažovanosť
    SOCIAL_RESPONSIBILITY = 'social_responsibility', // Sociálna zodpovednosť
    DEMOCRATIC_PARTICIPATION = 'democratic_participation' // Demokratická participácia
}

export const EntrepreneurshipCategoryLabels: Record<EntrepreneurshipCategory, string> = {
    [EntrepreneurshipCategory.INITIATIVE]: 'Iniciatíva',
    [EntrepreneurshipCategory.RISK_TAKING]: 'Ochota riskovať',
    [EntrepreneurshipCategory.INNOVATION]: 'Inovácia',
    [EntrepreneurshipCategory.PROBLEM_SOLVING]: 'Riešenie problémov',
    [EntrepreneurshipCategory.COMMUNITY_INVOLVEMENT]: 'Komunitná angažovanosť',
    [EntrepreneurshipCategory.SOCIAL_RESPONSIBILITY]: 'Sociálna zodpovednosť',
    [EntrepreneurshipCategory.DEMOCRATIC_PARTICIPATION]: 'Demokratická participácia'
}

export const EntrepreneurshipCategoryDescriptions: Record<EntrepreneurshipCategory, string> = {
    [EntrepreneurshipCategory.INITIATIVE]: 'Proaktívny prístup a schopnosť začať nové projekty',
    [EntrepreneurshipCategory.RISK_TAKING]: 'Odvaha riskovať a experimentovať s novými nápadmi',
    [EntrepreneurshipCategory.INNOVATION]: 'Kreatívne myslenie a hľadanie nových riešení',
    [EntrepreneurshipCategory.PROBLEM_SOLVING]: 'Schopnosť identifikovať a riešiť problémy',
    [EntrepreneurshipCategory.COMMUNITY_INVOLVEMENT]: 'Aktívna účasť v komunite a pomoc okoliu',
    [EntrepreneurshipCategory.SOCIAL_RESPONSIBILITY]: 'Uvedomenie si dopadu na spoločnosť',
    [EntrepreneurshipCategory.DEMOCRATIC_PARTICIPATION]: 'Zapájanie sa do demokratických procesov'
}

export interface EntrepreneurshipQuestionType {
    id: string
    title: string
    category: EntrepreneurshipCategory
    type: SurveyQuestionTypes
    reversed: boolean
    value?: number // User's answer (1-5)
}

export interface EntrepreneurshipScores {
    [EntrepreneurshipCategory.INITIATIVE]: number
    [EntrepreneurshipCategory.RISK_TAKING]: number
    [EntrepreneurshipCategory.INNOVATION]: number
    [EntrepreneurshipCategory.PROBLEM_SOLVING]: number
    [EntrepreneurshipCategory.COMMUNITY_INVOLVEMENT]: number
    [EntrepreneurshipCategory.SOCIAL_RESPONSIBILITY]: number
    [EntrepreneurshipCategory.DEMOCRATIC_PARTICIPATION]: number
}

// Behavioral scenario types - reveal actual behavior vs stated beliefs
export interface EntrepreneurshipScenarioOption {
    id: string
    text: string
    level: 'low' | 'medium' | 'high' // Entrepreneurial/civic engagement level
}

export interface EntrepreneurshipScenario {
    id: string
    category: EntrepreneurshipCategory
    type: 'scenario'
    scenario: string
    question: string
    options: EntrepreneurshipScenarioOption[]
    selectedOptionId?: string
}

export type EntrepreneurshipSurveyItem = EntrepreneurshipQuestionType | EntrepreneurshipScenario

export function isEntrepreneurshipScenario(item: EntrepreneurshipSurveyItem): item is EntrepreneurshipScenario {
    return 'type' in item && item.type === 'scenario'
}

// 35 questions total
// Entrepreneurship: 20 questions (5 per dimension)
// Civic Engagement: 15 questions (5 per dimension)
// Scale: 1 = Vôbec nesúhlasím, 5 = Úplne súhlasím
export const ENTREPRENEURSHIP_QUESTIONS: EntrepreneurshipQuestionType[] = [
    // INITIATIVE (Iniciatíva) - 5 questions
    {
        id: uuidv4(),
        title: 'Keď vidím problém, začnem hľadať riešenie bez toho, aby ma niekto žiadal',
        category: EntrepreneurshipCategory.INITIATIVE,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Rád/rada začínam nové projekty a aktivity',
        category: EntrepreneurshipCategory.INITIATIVE,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Radšej čakám, kým ma niekto požiada o pomoc',
        category: EntrepreneurshipCategory.INITIATIVE,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },
    {
        id: uuidv4(),
        title: 'Vidím príležitosti tam, kde ich iní nevidia',
        category: EntrepreneurshipCategory.INITIATIVE,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Som aktívny/aktívna v hľadaní nových možností pre seba a iných',
        category: EntrepreneurshipCategory.INITIATIVE,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },

    // RISK TAKING (Ochota riskovať) - 5 questions
    {
        id: uuidv4(),
        title: 'Som ochotný/ochotná riskovať pre dosiahnutie cieľov',
        category: EntrepreneurshipCategory.RISK_TAKING,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Nebojím sa neúspechu, keď skúšam niečo nové',
        category: EntrepreneurshipCategory.RISK_TAKING,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Preferujem istotu pred novými výzvami',
        category: EntrepreneurshipCategory.RISK_TAKING,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },
    {
        id: uuidv4(),
        title: 'Experimentujem s novými nápadmi, aj keď nie sú overené',
        category: EntrepreneurshipCategory.RISK_TAKING,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Radšej sa držím osvedčených postupov',
        category: EntrepreneurshipCategory.RISK_TAKING,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },

    // INNOVATION (Inovácia) - 5 questions
    {
        id: uuidv4(),
        title: 'Rád/rada vymýšľam nové spôsoby riešenia problémov',
        category: EntrepreneurshipCategory.INNOVATION,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Hľadám kreatívne riešenia namiesto štandardných postupov',
        category: EntrepreneurshipCategory.INNOVATION,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Tradičné metódy mi vyhovujú viac ako experimenty',
        category: EntrepreneurshipCategory.INNOVATION,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },
    {
        id: uuidv4(),
        title: 'Mám veľa nápadov na zlepšenie vecí okolo seba',
        category: EntrepreneurshipCategory.INNOVATION,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Snažím sa nájsť inovatívne prístupy k bežným úlohám',
        category: EntrepreneurshipCategory.INNOVATION,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },

    // PROBLEM SOLVING (Riešenie problémov) - 5 questions
    {
        id: uuidv4(),
        title: 'Výzvy ma motivujú k hľadaniu riešení',
        category: EntrepreneurshipCategory.PROBLEM_SOLVING,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Dokážem analyzovať komplexné problémy a nájsť riešenie',
        category: EntrepreneurshipCategory.PROBLEM_SOLVING,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Keď narazím na problém, cítim sa frustrovaný/frustrovaná',
        category: EntrepreneurshipCategory.PROBLEM_SOLVING,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },
    {
        id: uuidv4(),
        title: 'Rád/rada prepájam informácie z rôznych oblastí pri riešení problémov',
        category: EntrepreneurshipCategory.PROBLEM_SOLVING,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Vytrvám pri hľadaní riešenia, aj keď je to náročné',
        category: EntrepreneurshipCategory.PROBLEM_SOLVING,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },

    // COMMUNITY INVOLVEMENT (Komunitná angažovanosť) - 5 questions
    {
        id: uuidv4(),
        title: 'Pravidelne sa zapájam do komunitných aktivít',
        category: EntrepreneurshipCategory.COMMUNITY_INVOLVEMENT,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Aktívne pomáham ľuďom vo svojom okolí',
        category: EntrepreneurshipCategory.COMMUNITY_INVOLVEMENT,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Nemám čas na komunitné aktivity',
        category: EntrepreneurshipCategory.COMMUNITY_INVOLVEMENT,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },
    {
        id: uuidv4(),
        title: 'Cítim zodpovednosť za zlepšenie mojej komunity',
        category: EntrepreneurshipCategory.COMMUNITY_INVOLVEMENT,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Venujem čas dobrovoľníckym aktivitám',
        category: EntrepreneurshipCategory.COMMUNITY_INVOLVEMENT,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },

    // SOCIAL RESPONSIBILITY (Sociálna zodpovednosť) - 5 questions
    {
        id: uuidv4(),
        title: 'Zvažujem dopad svojich rozhodnutí na spoločnosť',
        category: EntrepreneurshipCategory.SOCIAL_RESPONSIBILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Je pre mňa dôležité prispievať k lepšej spoločnosti',
        category: EntrepreneurshipCategory.SOCIAL_RESPONSIBILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Sociálne problémy ma veľmi neznepokojujú',
        category: EntrepreneurshipCategory.SOCIAL_RESPONSIBILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },
    {
        id: uuidv4(),
        title: 'Snažím sa žiť udržateľným spôsobom',
        category: EntrepreneurshipCategory.SOCIAL_RESPONSIBILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Podporujem iniciatívy zamerané na spoločenské problémy',
        category: EntrepreneurshipCategory.SOCIAL_RESPONSIBILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },

    // DEMOCRATIC PARTICIPATION (Demokratická participácia) - 5 questions
    {
        id: uuidv4(),
        title: 'Pravidelne sa zúčastňujem volieb a referend',
        category: EntrepreneurshipCategory.DEMOCRATIC_PARTICIPATION,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Sledujem politické dianie a verejné záležitosti',
        category: EntrepreneurshipCategory.DEMOCRATIC_PARTICIPATION,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Politika a verejné záležitosti ma nezaujímajú',
        category: EntrepreneurshipCategory.DEMOCRATIC_PARTICIPATION,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },
    {
        id: uuidv4(),
        title: 'Vyjadrujem svoj názor na dôležité spoločenské témy',
        category: EntrepreneurshipCategory.DEMOCRATIC_PARTICIPATION,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Aktívne sa zapájam do občianskych iniciatív',
        category: EntrepreneurshipCategory.DEMOCRATIC_PARTICIPATION,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    }
]

// 7 Behavioral scenarios - one per category
// Reveal actual behavior vs stated beliefs (weighted 1.5x in scoring)
export const ENTREPRENEURSHIP_SCENARIOS: EntrepreneurshipScenario[] = [
    // INITIATIVE scenario
    {
        id: uuidv4(),
        category: EntrepreneurshipCategory.INITIATIVE,
        type: 'scenario',
        scenario:
            'V škole alebo práci si si všimol/všimla, že systém na evidenciu dochádzky nefunguje správne. Kolegovia sa už niekoľko týždňov sťažujú, ale nikto nič nerobí. IT oddelenie tvrdí, že má iné priority. Máš nápad, ako by sa dal problém vyriešiť jednoduchým workaroundom.',
        question: 'Čo urobíš?',
        options: [
            {
                id: uuidv4(),
                text: 'Počkám, kým sa toho niekto chopí. Nie je to moja zodpovednosť a nechcem na seba brať prácu navyše.',
                level: 'low'
            },
            {
                id: uuidv4(),
                text: 'Napíšem email IT oddeleniu s mojim nápadom a počkám na ich odpoveď.',
                level: 'medium'
            },
            {
                id: uuidv4(),
                text: 'Vytvorím prototype riešenia, otestujem ho a predložím ho s konkrétnymi výsledkami. Ak to funguje, pomôžem s implementáciou.',
                level: 'high'
            }
        ]
    },
    // RISK TAKING scenario
    {
        id: uuidv4(),
        category: EntrepreneurshipCategory.RISK_TAKING,
        type: 'scenario',
        scenario:
            'Dostal/dostala si ponuku na zaujímavú pracovnú pozíciu v novej firme. Ponúkajú ti o 40% vyšší plat, ale firma funguje len dva roky a nie je isté, či bude úspešná. V súčasnej práci máš istotu, ale cítiš, že stagneješ. Máš mesiac na rozhodnutie.',
        question: 'Ako sa rozhodneš?',
        options: [
            {
                id: uuidv4(),
                text: 'Zostanem v súčasnej práci. Istota je dôležitejšia ako vyšší plat. Kto vie, čo sa môže stať s novou firmou.',
                level: 'low'
            },
            {
                id: uuidv4(),
                text: 'Prehovorím si to s rodinou a priateľmi. Ak mi väčšina poradí zostať, zostanem. Ak pôjsť, pôjdem.',
                level: 'medium'
            },
            {
                id: uuidv4(),
                text: 'Preskúmam novú firmu, stretnem sa s tímom, zanalyzujem riziká. Ak vidím potenciál a verím konceptu, riziko podstúpim.',
                level: 'high'
            }
        ]
    },
    // INNOVATION scenario
    {
        id: uuidv4(),
        category: EntrepreneurshipCategory.INNOVATION,
        type: 'scenario',
        scenario:
            'Tvoj tím už rok používa ten istý spôsob na prípravu prezentácií pre klientov. Funguje to, ale je to zdĺhavé - každá prezentácia trvá 3 hodiny. Všimol/všimla si si nový nástroj s AI, ktorý by to mohol urobiť za 30 minút, ale nikto iný v tíme ho nepozná.',
        question: 'Čo urobíš?',
        options: [
            {
                id: uuidv4(),
                text: 'Nebudem meniť zabehaný systém. Funguje, kolegovia sú naň zvyknutí a zmena by spôsobila chaos.',
                level: 'low'
            },
            {
                id: uuidv4(),
                text: 'Vyskúšam nový nástroj pre seba. Ak mi bude vyhovovať, možno o ňom poviem kolegom.',
                level: 'medium'
            },
            {
                id: uuidv4(),
                text: 'Naučím sa nový nástroj, pripravím ukážku pre tím a navrhnem pilotný projekt. Ak sa osvedčí, pomôžem s prechodom celého tímu.',
                level: 'high'
            }
        ]
    },
    // PROBLEM SOLVING scenario
    {
        id: uuidv4(),
        category: EntrepreneurshipCategory.PROBLEM_SOLVING,
        type: 'scenario',
        scenario:
            'Organizuješ podujatie pre 50 ľudí. Dva dni pred akciou ti katering zavolá, že rušia objednávku kvôli vnútorným problémom. Všetky alternatívne cateringy sú buď obsadené, alebo príliš drahé. Podujatie je dôležité a zrušiť ho nie je možné.',
        question: 'Ako zareaguješ?',
        options: [
            {
                id: uuidv4(),
                text: 'Cítim sa frustrovaný/á a bezmocný/á. Zavolám organizátorovi a poviem, že občerstvenie nebude. Nie je to moja chyba.',
                level: 'low'
            },
            {
                id: uuidv4(),
                text: 'Napíšem na sociálne siete a požiadam priateľov o odporúčania. Možno niekto pozná niekoho, kto by mohol pomôcť.',
                level: 'medium'
            },
            {
                id: uuidv4(),
                text: 'Systematicky zanalyzujem alternatívy: lokálne reštaurácie s rozvozom, domáci catéring, dobrovoľníci s varením. Kombinujem viac menších riešení, aby som dosiahol/a výsledok.',
                level: 'high'
            }
        ]
    },
    // COMMUNITY INVOLVEMENT scenario
    {
        id: uuidv4(),
        category: EntrepreneurshipCategory.COMMUNITY_INVOLVEMENT,
        type: 'scenario',
        scenario:
            'V tvojej štvrti sa má postaviť nové ihrisko pre deti. Mestská časť hľadá dobrovoľníkov na organizáciu zbierky a koordináciu stavby. Projekt by trval 3 mesiace a vyžadoval by asi 5 hodín týždenne tvojho času.',
        question: 'Čo urobíš?',
        options: [
            {
                id: uuidv4(),
                text: 'Nemám na to čas. Mám dosť vlastných starostí a verím, že sa nájde niekto iný. Prípadne prispiejem finančne.',
                level: 'low'
            },
            {
                id: uuidv4(),
                text: 'Prihlásim sa ako dobrovoľník na konkrétne úlohy - napríklad pomoc pri stavbe cez víkend. Na dlhodobú koordináciu ale nemám kapacitu.',
                level: 'medium'
            },
            {
                id: uuidv4(),
                text: 'Prihlásim sa do organizačného tímu. Cítim zodpovednosť za svoju komunitu a verím, že ihrisko bude prínosom pre všetky deti vrátane mojich.',
                level: 'high'
            }
        ]
    },
    // SOCIAL RESPONSIBILITY scenario
    {
        id: uuidv4(),
        category: EntrepreneurshipCategory.SOCIAL_RESPONSIBILITY,
        type: 'scenario',
        scenario:
            'Nakupuješ nové oblečenie. Vidíš tričko za 5€ od fast fashion značky a podobné tričko za 25€ od udržateľnej značky. Vieš, že lacná značka využíva lacnú pracovnú silu a neekologickú výrobu. Drahšia značka garantuje férové podmienky a recyklované materiály.',
        question: 'Ktoré si kúpiš?',
        options: [
            {
                id: uuidv4(),
                text: 'Kúpim lacnejšie. Potrebujem šetriť a nemôžem riešiť problémy celého sveta. Jeden človek nič nezmení.',
                level: 'low'
            },
            {
                id: uuidv4(),
                text: 'Záleží na situácii. Pri niektorých nákupoch si môžem dovoliť udržateľnú voľbu, pri iných nie. Snažím sa nájsť rovnováhu.',
                level: 'medium'
            },
            {
                id: uuidv4(),
                text: 'Kúpim drahšie alebo počkám na zľavu. Radšej mám menej kvalitných vecí. Moje nákupné rozhodnutia sú hlas, ktorým hlasujem za svet, aký chcem.',
                level: 'high'
            }
        ]
    },
    // DEMOCRATIC PARTICIPATION scenario
    {
        id: uuidv4(),
        category: EntrepreneurshipCategory.DEMOCRATIC_PARTICIPATION,
        type: 'scenario',
        scenario:
            'V tvojom meste sa schvaľuje nový územný plán, ktorý ovplyvní dopravu, zeleň a výstavbu na najbližších 20 rokov. Verejné prerokovanie je budúci utorok o 17:00. Máš možnosť prísť a vyjadriť svoj názor, ale je to v čase, keď by si normálne oddychoval/a po práci.',
        question: 'Pôjdeš na prerokovanie?',
        options: [
            {
                id: uuidv4(),
                text: 'Nepôjdem. Politici si aj tak urobia, čo chcú. Môj hlas nič nezmení a po práci potrebujem oddych.',
                level: 'low'
            },
            {
                id: uuidv4(),
                text: 'Prečítam si materiály online a možno napíšem email s pripomienkami. Na fyzické stretnutie to ale asi nevydám.',
                level: 'medium'
            },
            {
                id: uuidv4(),
                text: 'Pôjdem. Územný plán ovplyvní moje mesto na desaťročia. Chcem vedieť, čo sa plánuje, a vyjadriť svoj názor. Je to moja občianska povinnosť.',
                level: 'high'
            }
        ]
    }
]

// Fisher-Yates shuffle for randomizing items
function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const left = shuffled[i]
        const right = shuffled[j]

        if (left === undefined || right === undefined) {
            continue
        }

        shuffled[i] = right
        shuffled[j] = left
    }
    return shuffled
}

// Initialize a new Entrepreneurship survey
export function initializedEntrepreneurshipSurvey(userId: UserId): Omit<EntrepreneurshipSurvey, 'id' | 'createdAt'> {
    const questions = ENTREPRENEURSHIP_QUESTIONS.map((q) => ({ ...q, value: undefined }))
    const scenarios = ENTREPRENEURSHIP_SCENARIOS.map((s) => ({ ...s, selectedOptionId: undefined }))

    // Combine and shuffle questions + scenarios for varied presentation
    const allItems: EntrepreneurshipSurveyItem[] = shuffleArray([...questions, ...scenarios])

    return {
        surveyType: SurveyTypes.ENTREPRENEURSHIP,
        userId,
        hash: uuidv4(),
        questions,
        scenarios,
        allItems,
        scores: {
            [EntrepreneurshipCategory.INITIATIVE]: 0,
            [EntrepreneurshipCategory.RISK_TAKING]: 0,
            [EntrepreneurshipCategory.INNOVATION]: 0,
            [EntrepreneurshipCategory.PROBLEM_SOLVING]: 0,
            [EntrepreneurshipCategory.COMMUNITY_INVOLVEMENT]: 0,
            [EntrepreneurshipCategory.SOCIAL_RESPONSIBILITY]: 0,
            [EntrepreneurshipCategory.DEMOCRATIC_PARTICIPATION]: 0
        },
        entrepreneurshipScore: 0,
        civicEngagementScore: 0,
        isDraft: true,
        deletedAt: null
    }
}
